<script setup lang="ts">
/**
 * 源站 GameSearch 全屏层：.dialog-overlay z 1003 / bg #1d2027。
 * 450：nav 450×60、input 426×60 radius 9.6、推荐 3 列 131.3。
 */
import { computed, ref, watch } from 'vue';
import { t } from '@/shared/i18n/messages';
import { useRouter } from 'vue-router';
import { listGames } from '@/features/game/game-api';
import { toOroMxGame } from '@/features/game/oro-mx-catalog';
import OroMxLobbyCardImage from '@/pages/home/OroMxLobbyCardImage.vue';
import { oroMxAssetPaths, type OroMxGame } from '@/shared/theme/oro-mx-home-data';
import { useOroMxShellStore } from '@/stores/oro-mx-shell-store';

const router = useRouter();
const shell = useOroMxShellStore();
const query = ref('');
const recommended = ref<OroMxGame[]>([]);
const results = ref<OroMxGame[]>([]);
const searching = ref(false);

const keyword = computed(() => query.value.trim());
const canSearch = computed(() => keyword.value.length >= 2);
let searchTimer: number | undefined;

async function loadRecommended() {
  try {
    const page = await listGames({ collection: 'popular', pageNo: 1, pageSize: 60 });
    recommended.value = page.items.map(toOroMxGame);
  } catch {
    recommended.value = [];
  }
}

async function runSearch() {
  if (!canSearch.value) {
    results.value = [];
    return;
  }
  searching.value = true;
  try {
    const page = await listGames({ keyword: keyword.value, pageNo: 1, pageSize: 60 });
    results.value = page.items.map(toOroMxGame);
  } catch {
    results.value = [];
  } finally {
    searching.value = false;
  }
}

watch(
  () => shell.searchOpen,
  (open) => {
    if (open && !recommended.value.length) void loadRecommended();
  },
);

watch(keyword, () => {
  window.clearTimeout(searchTimer);
  if (!canSearch.value) {
    results.value = [];
    return;
  }
  searchTimer = window.setTimeout(() => {
    void runSearch();
  }, 300);
});

function close() {
  query.value = '';
  results.value = [];
  shell.closeSearch();
}

function openGame(id: string) {
  query.value = '';
  results.value = [];
  shell.closeSearch();
  void router.push(`/gameDetails/${encodeURIComponent(id)}`);
}
</script>

<template>
  <div v-if="shell.searchOpen" class="dialog-overlay">
    <div class="dialog-content-mb">
      <div class="mb_nav_bar">
        <button class="home_m_lobby_icon_btn" type="button" aria-label="back" @click="close">
          <svg class="icon-arrow" viewBox="0 0 24 24" aria-hidden="true">
            <use :href="`${oroMxAssetPaths.sprite}#icon-arrow-left-small`" />
          </svg>
        </button>
        <div class="nav-title">{{ t('oro.search.title') }}</div>
      </div>
      <div class="dialog-content-wrap">
        <div class="game-search">
          <label class="game_search_input">
            <svg class="search-ico" viewBox="0 0 1024 1024" aria-hidden="true">
              <path
                fill="currentColor"
                d="m795.904 750.72 124.992 124.928a32 32 0 0 1-45.248 45.248L750.656 795.904a416 416 0 1 1 45.248-45.248zM480 832a352 352 0 1 0 0-704 352 352 0 0 0 0 704"
              />
            </svg>
            <input
              v-model="query"
              class="game_search_field"
              type="search"
              autocomplete="off"
              :placeholder="t('oro.search.placeholder')"
            />
          </label>
          <p v-if="!canSearch" class="game_search_tips">{{ t('oro.search.minChars') }}</p>
          <section v-if="!canSearch" class="game_search_section">
            <div class="game_search_section_head">
              <div class="game_search_section_title">
                <svg class="icon-arrow" viewBox="0 0 27 24" aria-hidden="true">
                  <use :href="`${oroMxAssetPaths.sprite}#icon-games-num`" />
                </svg>
                <span>{{ t('oro.search.recommended') }}</span>
              </div>
            </div>
            <p v-if="!recommended.length" class="game_search_tips">{{ t('oro.common.empty') }}</p>
            <div v-else class="game_search_grid">
              <button
                v-for="game in recommended"
                :key="game.id"
                class="home_lobby_card"
                type="button"
                :aria-label="game.title"
                @click="openGame(game.id)"
              >
                <OroMxLobbyCardImage :src="game.image" :alt="game.title" />
              </button>
            </div>
          </section>
          <p v-else-if="!searching && !results.length" class="game_search_tips">{{ t('oro.common.empty') }}</p>
          <div v-else class="game_search_grid">
            <button
              v-for="game in results"
              :key="game.id"
              class="home_lobby_card"
              type="button"
              :aria-label="game.title"
              @click="openGame(game.id)"
            >
              <OroMxLobbyCardImage :src="game.image" :alt="game.title" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 1003;
  display: flex;
  flex-direction: column;
}

.dialog-content-mb {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: #1d2027;
}

.mb_nav_bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 2.6667rem;
  color: #fff;
}

.home_m_lobby_icon_btn {
  position: absolute;
  top: 0.5333rem;
  left: 0.5333rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  color: #fff;
  background: #2e2e36;
  border: 1px solid rgb(117 117 119 / 18%);
  border-radius: 0.32rem;
}

.icon-arrow {
  width: 1.3333rem;
  height: 1.3333rem;
  fill: currentColor;
}

.nav-title {
  color: #fff;
  font-size: 1.0667rem;
  font-weight: 500;
  line-height: 1.6rem;
}

.dialog-content-wrap {
  flex: 1;
  width: 100%;
  min-height: 0;
  padding: 0.4267rem 0.5333rem calc(3.7333rem + env(safe-area-inset-bottom, 0px));
  overflow-x: hidden;
  overflow-y: auto;
}

.game-search {
  box-sizing: border-box;
  width: 100%;
  min-height: 5.3333rem;
  padding-bottom: 0.2133rem;
}

.game_search_input {
  display: flex;
  gap: 0.2133rem;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  height: 2.6667rem;
  margin-bottom: 0.5333rem;
  padding: 0 0.2933rem;
  background: #15161c;
  border: 0.0533rem solid rgb(117 117 119 / 18%);
  border-radius: 0.4267rem;
}

.search-ico {
  flex-shrink: 0;
  width: 0.7467rem;
  height: 0.7467rem;
  color: #a8abb2;
}

.game_search_field {
  flex: 1;
  min-width: 0;
  height: 0.8rem;
  padding: 0;
  color: #fff;
  font-size: 0.7467rem;
  line-height: 0.8rem;
  background: transparent;
  border: 0;
  outline: none;
}

.game_search_field::placeholder {
  color: #757577;
}

.game_search_tips {
  margin: 0 0 0.4rem;
  color: #757577;
  font-size: 0.6933rem;
  line-height: 1.04rem;
  text-align: center;
}

.game_search_section_head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 1.6rem;
  margin: 0.5067rem 0;
}

.game_search_section_title {
  display: flex;
  gap: 0.5333rem;
  align-items: center;
  color: #fff;
  font-size: 0.6933rem;
  font-weight: 600;
  line-height: 1.04rem;
}

.game_search_section_title .icon-arrow {
  width: 1.0667rem;
  height: 1.0667rem;
}

.game_search_grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.7111rem;
}

.home_lobby_card {
  overflow: hidden;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0.2667rem;
}

.home_lobby_card_media {
  overflow: hidden;
  aspect-ratio: 1;
  border-radius: 0.4267rem;
}

.home_lobby_card_img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
